import Layout from "@/components/layout/Layout";
import React, { useState } from "react";
import Content from "@/components/layout/Content";
import ContentOptions from "@/components/layout/ContentOptions";
import Heading from "@/components/elements/Heading";
import Sidepanel from "@/components/layout/Sidepanel";
import Label from "@/components/elements/Label";
import { InertiaLink } from "@inertiajs/inertia-react";
import TextInput from "@/components/elements/TextInput";
import DashboardMenu from "@/pages/Dashboard/DashboardMenu";
import api from "@/services/api";

/**
 * EditColor Dashboard Component
 *
 * Provides an interface for editing existing color records in the system.
 * Allows modification of color properties including RAL codes, Trespa codes,
 * hex values, delivery time settings, and external visibility.
 *
 * @param {Object} props - Component props
 * @param {Object} props.colorobject - The color object to edit
 * @param {string} props.colorobject.id - Color ID
 * @param {string} props.colorobject.ral - RAL color code
 * @param {string} props.colorobject.trespa - Trespa color code
 * @param {string} props.colorobject.hex - Hex color value
 * @param {boolean} props.colorobject.levertijd - Long delivery time flag
 * @param {boolean} props.colorobject.extern - External visibility flag
 * @param {string} props.updateColorUrl - API endpoint for updating the color
 *
 * @returns {JSX.Element} The rendered edit color form page
 */
export default function Newcolor({ colorobject, updateColorUrl }) {

    // Initialize state with existing color data
    const [color, setColor] = useState(colorobject);
    const [error, setError] = useState([]);

    /**
     * Handle form submission to save color changes
     * Sends PUT request to update the color and redirects on success
     *
     * @param {Event} e - Form submit event
     */
    function savecolor(e) {
        e.preventDefault();
        api
            .put(updateColorUrl, color)
            .then((response) => {
                window.location.href = "/dashboard/colors";
            })
            .catch((error) => setError(error.response.data.errors));
    }

    /**
     * Update color state with new values from TextInput components
     * Used by TextInput components for text field updates
     *
     * @param {Object} newcolor - Updated color object
     */
    function updateColor(newcolor) {
        setColor(newcolor);
    }

    /**
     * Handle checkbox changes for boolean properties
     * Updates levertijd and extern properties based on checkbox state
     *
     * @param {Event} event - Checkbox change event
     * @param {string} property - Property name being updated
     */
    function updateColorBoolean(event, property) {
        const target = event.target;
        const value =
            target.type === "checkbox" ? target.checked : target.value;
        const name = target.name;

        let colorprop = { ...color };
        colorprop[name] = value;
        setColor(colorprop);
    }

    return (
        <Layout>
            <Sidepanel>
                <DashboardMenu active={"Kleuren"} />
            </Sidepanel>
            <Content>
                <ContentOptions small>
                    <Heading heading={"Nieuwe gebruiker"} />
                </ContentOptions>
                <div
                    className={`contentoptions ingaveoptions col-md-9 d-flex flex-column`}
                >
                    <Heading heading="Pas kleur aan">
                        <div>
                            <InertiaLink
                                href="/dashboard/colors"
                                className="btn-orange"
                            >
                                terug
                            </InertiaLink>
                            <a
                                name={"Opslaan"}
                                href="#"
                                onClick={savecolor}
                                className="btn-orange"
                            >
                                opslaan
                            </a>
                        </div>
                    </Heading>

                    <div className="details">
                        <div className="question mb-3">
                            <Label label="Ral" small />
                            <TextInput
                                properties={color}
                                property="ral"
                                updateProperties={updateColor}
                            />
                            {error && error.ral ? (
                                <label className="labelsmall text-danger">
                                    {error.ral[0]}
                                </label>
                            ) : (
                                ""
                            )}
                        </div>
                        <div className="question mb-3">
                            <Label label="Trespa" small />
                            <TextInput
                                properties={color}
                                property="trespa"
                                updateProperties={updateColor}
                            />
                            {error && error.trespa ? (
                                <label className="labelsmall text-danger">
                                    {error.trespa[0]}
                                </label>
                            ) : (
                                ""
                            )}
                        </div>
                        <div className="question mb-3">
                            <Label label="Hex" small />
                            <TextInput
                                properties={color}
                                property="hex"
                                updateProperties={updateColor}
                            />
                            {error && error.hex ? (
                                <label className="labelsmall text-danger">
                                    {error.hex[0]}
                                </label>
                            ) : (
                                ""
                            )}
                        </div>

                        <div className="question mb-3">
                            <Label label="Lange levertijd" small />
                            <input
                                name="levertijd"
                                className="ml-3"
                                type="checkbox"
                                checked={color.levertijd}
                                onChange={updateColorBoolean}
                            />
                        </div>

                        <div className="question mb-3">
                            <Label
                                label="KLeur zichtbaar voor alle klanten"
                                small
                            />
                            <input
                                name="extern"
                                className="ml-3"
                                type="checkbox"
                                checked={color.extern}
                                onChange={updateColorBoolean}
                            />
                        </div>
                    </div>
                </div>
            </Content>
        </Layout>
    );
}
